// src/App.js
import React, { useEffect, useRef, useState, useContext, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { SEO } from "./components/LangSeo"
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"
import LanguageSelector from "./components/language-selector"
import { motion, AnimatePresence } from "framer-motion"
import { IoLogoWhatsapp, IoMdCloseCircle } from "react-icons/io"
import { FaArrowUp, FaBars } from "react-icons/fa"
import "swiper/css"
import { routesMap, lazyRoutes, sharedComponents } from "./routesMap"
import { loadTranslations } from "./components/translations"
import Loader from "./components/Loader"
import i18n from "./i18n"
import { TranslationProvider } from "./components/TranslationContext"

// ----------------------------------------------------
// Context and Helpers
// ----------------------------------------------------
export const AppContext = React.createContext(null)

export const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue)
  const toggle = (newValue) => {
    setValue((prevValue) =>
      typeof newValue === "boolean" ? newValue : !prevValue
    )
  }
  return [value, toggle]
}

const stripLang = (path) => path.replace(/^\/(en)/, "") || "/"
const getLocalizedPath = (path, language) => {
  if (routesMap[path]) {
    return routesMap[path][language] || path
  }
  return path
}
const navOrder = ["/", "/about", "/menu-carta", "/privacy"]
const getPageIndex = (path) => {
  const cleanPath = stripLang(path)
  return navOrder.indexOf(cleanPath)
}

const buttonVariants = {
  hidden: { y: "100%", opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.32, 0.72, 0, 1],
      opacity: { duration: 0.3 },
    },
  },
  exit: {
    y: "100%",
    opacity: 0,
    transition: {
      duration: 0.8,
      ease: [0.32, 0.72, 0, 1],
      opacity: { duration: 0.3 },
    },
  },
}

/* comment */

const getPageVariants = (direction) => ({
  initial: {
    x: direction > 0 ? "100%" : "-100%",
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: 1,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.32, 0.72, 0, 1],
      opacity: { duration: 0.3 },
    },
    zIndex: 2,
  },
  exit: {
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: 1,
    transition: {
      duration: 0.8,
      ease: [0.32, 0.72, 0, 1],
      opacity: { duration: 0.3 },
    },
  },
})

// ----------------------------------------------------
// Page Transition Wrapper (modified to accept skipTransition)
// ----------------------------------------------------
const PageTransitionWrapper = ({
  children,
  direction,
  setIsTransitioning,
  skipTransition,
}) => {
  const variants = skipTransition
    ? {
        initial: { x: 0, opacity: 1 },
        animate: { x: 0, opacity: 1 },
        exit: { x: 0, opacity: 1 },
      }
    : getPageVariants(direction)
  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      onAnimationStart={() => setIsTransitioning(true)}
      onAnimationComplete={() => setIsTransitioning(false)}
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      {children}
    </motion.div>
  )
}

// ----------------------------------------------------
// Custom NavLink Component
// ----------------------------------------------------
export const NavLink = ({ to, children, className = "", onClick, ...rest }) => {
  const location = useLocation()
  const { t, i18n } = useTranslation("global")
  const { closeMenu, setDirection } = useContext(AppContext)
  const navigate = useNavigate()
  const localizedTo = getLocalizedPath(to, i18n.language)

  const handleClick = (e) => {
    e.preventDefault()
    if (to.startsWith("#")) {
      const target = document.querySelector(to)
      if (target) {
        target.scrollIntoView({ behavior: "smooth" })
        closeMenu()
      }
      if (onClick) onClick(e)
      return
    }
    window.scrollTo({ top: 0, behavior: "instant" })
    const currentIndex = getPageIndex(location.pathname)
    const newIndex = getPageIndex(localizedTo)
    const dir = newIndex > currentIndex ? 1 : -1
    setDirection(dir)
    if (location.pathname !== localizedTo) {
      navigate(localizedTo)
      closeMenu()
    }
    if (onClick) onClick(e)
  }

  const isActive = stripLang(location.pathname) === stripLang(localizedTo)
  const baseClasses =
    "block hover:text-secondary-400 transition-colors duration-300"
  const activeClasses = isActive ? "underline" : ""

  return (
    <Link
      to={localizedTo}
      className={`${baseClasses} ${activeClasses} ${className}`.trim()}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Link>
  )
}

// ----------------------------------------------------
// Main App Component
// ----------------------------------------------------
function App() {
  const { Home, Listino, PrivacyPolicy, NotFound } = lazyRoutes

  const { ContactForm, Footer } = sharedComponents
  const { t, i18n } = useTranslation("global")
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const [isEditMode, setIsEditMode] = useState(false)

  // Manage edit mode via URL hash
  useEffect(() => {
    const handleHashChange = () => {
      setIsEditMode(window.location.hash === "#translate")
    }
    handleHashChange()
    window.addEventListener("hashchange", handleHashChange)
    return () => {
      window.removeEventListener("hashchange", handleHashChange)
    }
  }, [])

  // Load translations dynamically
  useEffect(() => {
    const load = async () => {
      try {
        const translations = await loadTranslations(i18n.language)
        i18n.addResourceBundle(
          i18n.language,
          "global",
          translations,
          true,
          true
        )
      } catch (error) {
        console.error("Error loading translations:", error)
      }
    }
    load()
  }, [i18n.language, i18n])

  // Page transition direction
  const [direction, setDirection] = useState(1)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Nav scroll & menu handling
  const navRef = useRef(null)
  const prevScrollPos = useRef(0)
  const [isAtTop, setIsAtTop] = useState(true)
  const [isNavHidden, setIsNavHidden] = useState(false)
  const scrollThreshold = 0

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isMenuOpen])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY
      const scrollDifference = currentScrollPos - prevScrollPos.current
      setIsAtTop(currentScrollPos === 0)
      if (!isMenuOpen) {
        if (currentScrollPos > scrollThreshold && scrollDifference > 0) {
          setIsNavHidden(true)
        } else if (scrollDifference < 0) {
          setIsNavHidden(false)
        }
      }
      prevScrollPos.current = currentScrollPos
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isMenuOpen])

  // Inverse color for certain pages
  const [isInverse, setIsInverse] = useState(false)
  useEffect(() => {
    const path = location.pathname
    const strippedPath = stripLang(path)
    const inversePages = ["/privacy", "/404", "/recensioni"]
    const inverseEnglishPages = ["/en/privacy", "/en/404", "/en/reviews"]
    const inverseItalianPages = ["/privacy", "/404"]
    if (
      inversePages.includes(strippedPath) ||
      inverseEnglishPages.includes(path) ||
      inverseItalianPages.includes(strippedPath)
    ) {
      setIsInverse(true)
    } else {
      setIsInverse(false)
    }
  }, [location.pathname])

  // Language & path management (keeps URL and language in sync)
  useEffect(() => {
    const path = location.pathname
    const isEnglishPath = path.startsWith("/en")
    if (isEnglishPath && i18n.language !== "en") {
      i18n.changeLanguage("en").then(() => {
        const currentStripped = stripLang(path)
        const localized = getLocalizedPath(currentStripped, "en")
        if (localized !== path) {
          navigate(localized, { replace: true })
        }
      })
    } else if (!isEnglishPath && i18n.language !== "it") {
      i18n.changeLanguage("it").then(() => {
        const currentStripped = stripLang(path)
        const localized = getLocalizedPath(currentStripped, "it")
        if (localized !== path) {
          navigate(localized, { replace: true })
        }
      })
    }
  }, [])

  // ----- NEW: Skip transition on language change -----
  const [skipTransition, setSkipTransition] = useState(false)
  useEffect(() => {
    const handleLanguageChanged = () => {
      setSkipTransition(true)
    }
    i18n.on("languageChanged", handleLanguageChanged)
    return () => {
      i18n.off("languageChanged", handleLanguageChanged)
    }
  }, [])

  useEffect(() => {
    if (skipTransition) {
      setSkipTransition(false)
    }
  }, [location])

  useEffect(() => {
    if (!skipTransition) {
      window.scrollTo({ top: 0, behavior: "instant" })
    }
  }, [location, skipTransition])
  // ----- END SKIP TRANSITION -----

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)
  const closeMenu = () => setIsMenuOpen(false)

  // Mobile swipe handling
  const startY = useRef(null)
  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY
  }
  const handleTouchMove = (e) => {
    if (startY.current === null) return
    const currentY = e.touches[0].clientY
    const diff = currentY - startY.current
    if (diff > 50) {
      closeMenu()
      startY.current = null
    }
  }
  const handleTouchEnd = () => {
    startY.current = null
  }

  const navClasses = `
    fixed top-0 left-0 font-body right-0 w-full z-40 
    transition-transform duration-300 ease-in-out
    ${isNavHidden && !isMenuOpen ? "-translate-y-full" : "translate-y-0"}
    ${
      window.scrollY < 150 && !isMenuOpen && !isInverse
        ? "bg-transparent text-white"
        : "text-secondary-200"
    }
    ${
      isMenuOpen
        ? "bg-background-light/40 backdrop-blur-sm text-secondary-200"
        : ""
    }
    ${isInverse ? "text-inverse-text" : ""}
    px-4 py-2
  `.trim()

  const [showButtons, setShowButtons] = useState(false)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY
      const shouldShow = currentScrollPos > 150 && !isNavHidden
      setShowButtons(shouldShow)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isNavHidden])

  const [isLoading, setIsLoading] = useState(true)
  const handleLoaderFinish = () => {
    setIsLoading(false)
  }

  useEffect(() => {
    if (!isLoading) {
      window.scrollTo({ top: 0, behavior: "instant" })
    }
  }, [location, isLoading])

  const [showPopup, setShowPopup] = useState(false)
  const popupEnabled =
    t("home.popup.enabled", { defaultValue: "true" }).toLowerCase() === "true"
  useEffect(() => {
    const lastDismissed = localStorage.getItem("popupDismissedDate")
    const today = new Date().toISOString().split("T")[0]
    if (lastDismissed === today) return
    const handlePopupScroll = () => {
      if (window.scrollY > 50) {
        setShowPopup(true)
        window.removeEventListener("scroll", handlePopupScroll)
      }
    }
    window.addEventListener("scroll", handlePopupScroll, { passive: true })
    return () => window.removeEventListener("scroll", handlePopupScroll)
  }, [])

  const closePopup = () => {
    const today = new Date().toISOString().split("T")[0]
    localStorage.setItem("popupDismissedDate", today)
    setShowPopup(false)
  }

  return (
    <div
      className={`relative bg-background-light min-h-screen ${
        isTransitioning ? "overflow-hidden" : ""
      }`}
    >
      {isLoading && <Loader onFinish={handleLoaderFinish} />}
      {!isLoading && (
        <>
          <AnimatePresence>
            {showButtons && (
              <motion.button
                variants={buttonVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="bg-secondary-400 hover:bg-secondary-200 transition-colors duration-300 ease-custom-easing text-white p-2 z-50 fixed bottom-5 right-3"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <FaArrowUp />
              </motion.button>
            )}
          </AnimatePresence>
          <TranslationProvider>
            <AppContext.Provider
              value={{ closeMenu, setDirection, isEditMode, setIsEditMode }}
            >
              <nav ref={navRef} className={navClasses}>
                <div
                  className={`flex justify-between px-2 rounded-md w-full ${
                    !isAtTop ? "bg-white/50 backdrop-blur-md" : ""
                  } items-center py-1 mx-auto`}
                >
                  <button
                    onClick={toggleMenu}
                    className="block xl:hidden z-[80] relative"
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                  >
                    {isMenuOpen ? (
                      <IoMdCloseCircle size={24} />
                    ) : (
                      <FaBars size={24} />
                    )}
                  </button>
                  <div className="flex justify-end text-white xl:justify-between items-center w-full">
                    <NavLink
                      to="/"
                      className="hidden xl:inline-block no-underline"
                    >
                      <img src="./img/logo-text.webp" className="w-40" />
                    </NavLink>
                    <ul
                      className={`${
                        isInverse ? "text-secondary-200" : ""
                      } hidden xl:flex space-x-2 2xl:space-x-6 `}
                    >
                      <NavLink
                        to="/"
                        className={`hidden xl:inline-block no-underline ${
                          isAtTop ? "text-white" : "text-red-900"
                        }`}
                      >
                        {t("nav.home")}
                      </NavLink>
                      <NavLink
                        to="/listino"
                        className={`hidden xl:inline-block no-underline ${
                          isAtTop ? "text-white" : "text-red-900"
                        }`}
                      >
                        {t("nav.listino")}
                      </NavLink>
                      <NavLink
                        to="#contatti"
                        className={`hidden xl:inline-block no-underline ${
                          isAtTop ? "text-white" : "text-red-900"
                        }`}
                      >
                        {t("nav.contact")}
                      </NavLink>
                    </ul>
                    <div className="flex items-center">
                      <a
                        href="https://wa.me/3398865139"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="z-50 text-2xl mr-2 transition-colors duration-700 hover:text-green-800 text-green-500"
                      >
                        <IoLogoWhatsapp />
                      </a>
                      <LanguageSelector />
                    </div>
                  </div>
                </div>
              </nav>
              {/*  <AnimatePresence>
              {showPopup && popupEnabled && (
                <motion.div
                  initial={{ y: "100%", opacity: 0 }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    transition: { duration: 0.5 },
                  }}
                  exit={{
                    y: "100%",
                    opacity: 0,
                    transition: { duration: 0.5 },
                  }}
                  className="z-40 fixed bottom-5 left-0 right-0 mx-auto bg-secondary-400 p-6 text-center max-w-screen-lg w-11/12 sm:w-auto shadow-lg rounded-lg"
                >
                  <button
                    onClick={closePopup}
                    className="absolute top-2 right-2 text-white hover:text-gray-200"
                  >
                    <IoMdCloseCircle size={24} />
                  </button>
                  <h4 className="text-white font-bold text-lg">
                    {t("home.popup.title")}
                  </h4>
                  <p className="text-white mt-2">
                    {t("home.popup.description")}
                  </p>
                  <NavLink
                    to={`/${t("home.popup.link")}`}
                    className="mt-4 inline-block underline text-white font-semibold hover:text-gray-200"
                    onClick={closePopup}
                  >
                    {t("home.popup.textLink")}
                  </NavLink>
                </motion.div>
              )}
            </AnimatePresence> */}
              <div
                className={`fixed top-0 left-0 right-0 bg-background-light text-secondary-200 z-50 transition-transform duration-500 ease-in-out xl:hidden h-screen pt-5 ${
                  isMenuOpen ? "translate-y-20" : "translate-y-full"
                } overflow-auto`}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="h-full flex flex-col justify-between">
                  <ul className="flex flex-col font-heading font-bold space-y-2 p-4 mt-5 text-center text-2xl">
                    <NavLink
                      to="/"
                      className="block hover:text-secondary-400 transition-colors duration-300"
                    >
                      {t("nav.home")}
                    </NavLink>{" "}
                    <NavLink
                      to="/listino"
                      className="block hover:text-secondary-400 transition-colors duration-300"
                    >
                      {t("nav.listino")}
                    </NavLink>
                    <NavLink to="/" className="no-underline text-secondary-200">
                      <img
                        src="./img/logo-color.webp"
                        className="w-full p-5 content-end"
                      />
                    </NavLink>
                  </ul>
                </div>
              </div>
              <AnimatePresence>
                <Routes location={location} key={location.pathname}>
                  <Route
                    path="/"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                          skipTransition={skipTransition}
                        >
                          <SEO page="home" image={"/img/out1.webp"} />
                          <Home />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/en"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                          skipTransition={skipTransition}
                        >
                          <SEO page="home" />
                          <Home />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />{" "}
                  <Route
                    path="/listino"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                          skipTransition={skipTransition}
                        >
                          <SEO page="listino" image={"/img/out1.webp"} />
                          <Listino />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/en/product-list"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                          skipTransition={skipTransition}
                        >
                          <SEO page="listino" />
                          <Listino />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  {/* Privacy Policy Routes */}
                  <Route
                    path="/privacy"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                        >
                          <SEO page="privacy" />
                          <PrivacyPolicy />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/en/privacy"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                        >
                          <SEO page="privacy" />
                          <PrivacyPolicy />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  {/* 404 Routes */}
                  <Route
                    path="/404"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                        >
                          <SEO page="404" />
                          <NotFound />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/en/404"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PageTransitionWrapper
                          direction={direction}
                          setIsTransitioning={setIsTransitioning}
                        >
                          <SEO page="404" />
                          <NotFound />
                          <ContactForm />
                          <Footer />
                        </PageTransitionWrapper>
                      </Suspense>
                    }
                  />
                  {/* Catch-all Route */}
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to={i18n.language === "en" ? "/en/404" : "/404"}
                        replace
                      />
                    }
                  />
                </Routes>
              </AnimatePresence>
              {/* Componente per la Modalità di Modifica */}
            </AppContext.Provider>
          </TranslationProvider>
        </>
      )}
    </div>
  )
}

export default App
